import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface WorkOrderProgress {
  currentStepIndex: number;
  completedSteps: number[];
  currentPhaseName: string;
  currentPhaseCategory: string;
  currentStepTitle?: string;
  totalSteps: number;
  completedCount: number;
  selectedErrorCodeIds?: string[];
  triageCompleted?: boolean;
  triageScore?: number;
  triageRecommendation?: string;
  harvestStep?: 1 | 2 | 3 | 4 | 5;
  sparePartIds?: string[];
  labelsPrinted?: boolean;
}

interface UseWorkOrderProgressResult {
  workOrderId: string | null;
  savedProgress: WorkOrderProgress | null;
  isLoading: boolean;
  saveProgress: (progress: WorkOrderProgress) => Promise<void>;
}

const isUuid = (id: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);

export function useWorkOrderProgress(
  applianceId: string | undefined,
  workOrderType: "repair" | "refurbish" | "harvest",
  requestedWorkOrderId?: string | null
): UseWorkOrderProgressResult {
  const { user } = useAuth();
  const [workOrderId, setWorkOrderId] = useState<string | null>(null);
  const [savedProgress, setSavedProgress] = useState<WorkOrderProgress | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Find the active work order for this appliance + type
  useEffect(() => {
    if (!user || !applianceId) {
      setIsLoading(false);
      return;
    }

    const fetchWorkOrder = async () => {
      let dbApplianceId = applianceId;

      // If the ID is not a UUID, resolve it via localStorage model number → DB
      if (!isUuid(applianceId)) {
        try {
          const stored = localStorage.getItem("recentlyAccessedAppliances");
          if (stored) {
            const appliances = JSON.parse(stored);
            const match = appliances.find((a: { id: string }) => a.id === applianceId);
            if (match?.modelNumber) {
              const { data: dbAppliance } = await supabase
                .from("appliances")
                .select("id")
                .eq("model_number", match.modelNumber)
                .eq("user_id", user.id)
                .limit(1);
              if (dbAppliance && dbAppliance.length > 0) {
                dbApplianceId = dbAppliance[0].id;
              }
            }
          }
        } catch { /* ignore */ }
      }

      if (requestedWorkOrderId) {
        const { data } = await supabase
          .from("work_orders")
          .select("id, data")
          .is("deleted_at", null)
          .eq("id", requestedWorkOrderId)
          .eq("user_id", user.id)
          .limit(1);

        if (data && data.length > 0) {
          setWorkOrderId(data[0].id);
          if (data[0].data && typeof data[0].data === "object") {
            setSavedProgress(data[0].data as unknown as WorkOrderProgress);
          }
        }
        setIsLoading(false);
        return;
      }

      const { data } = await supabase
        .from("work_orders")
        .select("id, data")
        .is("deleted_at", null)
        .eq("appliance_id", dbApplianceId)
        .eq("user_id", user.id)
        .eq("type", workOrderType)
        .in("status", ["in_progress", "pending"])
        .order("created_at", { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        setWorkOrderId(data[0].id);
        if (data[0].data && typeof data[0].data === "object") {
          setSavedProgress(data[0].data as unknown as WorkOrderProgress);
        }
      }
      setIsLoading(false);
    };

    fetchWorkOrder();
  }, [user, applianceId, workOrderType, requestedWorkOrderId]);

  // Debounced save to avoid excessive writes
  const saveProgress = useCallback(
    async (progress: WorkOrderProgress) => {
      if (!workOrderId) return;

      // Clear any pending save
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce: save after 500ms of no changes
      saveTimeoutRef.current = setTimeout(async () => {
        const { error } = await supabase
          .from("work_orders")
          .update({
            data: JSON.parse(JSON.stringify(progress)),
            status: progress.completedCount >= progress.totalSteps ? "completed" : "in_progress",
            completed_at: progress.completedCount >= progress.totalSteps ? new Date().toISOString() : null,
          })
          .eq("id", workOrderId);

        if (error) {
          console.error("Failed to save work order progress:", error);
        }
      }, 500);
    },
    [workOrderId]
  );

  return { workOrderId, savedProgress, isLoading, saveProgress };
}
